'use client'

import { useLocale } from '@/components/Link'
import LoadingIndicator from '@/components/LoadingIndicator'
import { useFragment } from '@/generated/gql/fragment-masking'
import { graphql } from '@/generated/gql/gql'
import { Language } from '@/generated/gql/graphql'
import { SPLITS, useFeatureFlag } from '@/helpers/useFeatureFlag'
import { getLangFromLocale } from '@fusion/locale/parsing'

import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import { useCallback, useEffect } from 'react'

import '../../../i18n/client'
import CanteenError from './components/CanteenError'
import { MenuView } from './components/MenuView'
import { MenuViewV2 } from './components/MenuViewV2'
import { useCcAppPromo } from './components/state/useCcAppPromo'
import { useCcMenuFiltersState } from './components/state/useCcMenuFiltersState'
import { useCcMenuState } from './components/state/useCcMenuState'
import type { CCMenuUserStateCustomError } from './components/state/useCcMenuUserState'
import {
  CCMenuUserStateCustomErrorCode,
  useCcMenuUserState,
} from './components/state/useCcMenuUserState'
import { useDefaultCurrency } from './components/state/useDefaultCurrency'
import { formatTimeslotTime } from './components/timeslotDates'

const canteenMenuCurrentMenuDataFragment = graphql(`
  fragment CanteenMenu_CurrentMenuDataFragment on CCMealService {
    ...CanteenMenuView_CurrentMenuDataFragment
  }
`)

const canteenMenuUserDataFragment = graphql(`
  fragment CanteenMenu_UserDataFragment on User {
    ...UseCanteenMenuFiltersState_UserDataFragment
    ...UseCanteenMenuState_UserDataFragment
    ...CanteenAccountLocation_UserDataFragment
    ...CanteenMenuView_UserDataFragment
    ...CanteenMenuView_UserDataFragmentV2
    ...UseCcAppPromo_UserDataFragment
    account {
      id
    }
  }
`)

export function Body() {
  const locale = useLocale()
  const params = useSearchParams()
  const pathname = usePathname()
  const router = useRouter()

  const paramsDate = params?.get('date') ?? ''
  const paramsMeal = params?.get('meal') ?? ''
  const paramsTimeslot = params?.get('timeslot') ?? ''
  const paramsVendors = params?.get('vendors') ?? ''
  const paramsDietaries = params?.get('dietaries') ?? ''
  const paramsAllergens = params?.get('allergens') ?? ''

  const {
    updateAccountLocation,
    updatingLocation,
    loading: loadingUserData,
    data: userDataProp,
    error: userDataError,
    called: userDataCalled,
    refetch: refetchUserData,
  } = useCcMenuUserState()
  const timezone = userDataProp?.accountLocation?.timezone
  const userData = useFragment(canteenMenuUserDataFragment, userDataProp)
  const useNewCcUi = useFeatureFlag(SPLITS.USE_NEW_CLOUD_CANTEEN_UI, {
    accountId: userData?.account?.id ?? '',
    userId: userDataProp?.id ?? '',
  })
  const appPromo = useCcAppPromo(userData)

  const {
    filters,
    budgetFilter,
    toggleBudgetFilter,
    toggleFilterValue,
    clearFilters,
    searchTerm,
    setSearchTerm,
  } = useCcMenuFiltersState({
    userData,
    paramsAllergens,
    paramsVendors,
    paramsDietaries,
  })

  const onSearchTermChange = useCallback(
    (value: string) => {
      if (value !== searchTerm) {
        setSearchTerm(value)
      }
    },
    [searchTerm, setSearchTerm],
  )

  const {
    date,
    meal,
    timeslot,
    loading: loadingMenuData,
    menuData: menuDataProp,
    menuClosed,
    error: menuDataError,
    orderTimeRemaining,
    refetch: refetchMenuData,
    updateSelectedService,
    updateTimeslot,
  } = useCcMenuState({ paramsDate, paramsMeal, paramsTimeslot, userData })
  const menuData = useFragment(canteenMenuCurrentMenuDataFragment, menuDataProp)

  const createQueryString = useCallback(
    (values: Record<string, string | number>) => {
      const paramsToSet = new URLSearchParams(params?.toString())
      Object.entries(values).forEach(([key, value]) => {
        if (value === '') paramsToSet.delete(key)
        else paramsToSet.set(key, value.toString())
      })
      return paramsToSet.toString()
    },
    [params],
  )

  useEffect(() => {
    if (userDataError?.name === CCMenuUserStateCustomErrorCode.AccountNotCCActive) {
      router.push('/office-catering/vendors')
    }
    if (userDataError?.name === CCMenuUserStateCustomErrorCode.SelectedLocationNotCCActive) {
      router.push('/authentication/cloud-canteen-signup-finish?destination=/canteen')
    }
  }, [userDataError, router])

  useEffect(() => {
    if (userDataError) return
    if (!date && !meal && !timeslot) return
    const newTimeslotHHmm = timeslot ? formatTimeslotTime(timeslot, timezone) : ''
    const newAllergens = filters.allergens.join(',')
    const newDietaries = filters.dietaries.join(',')
    const newVendors = filters.vendors.join(',')
    const newParams = {
      ...(timeslot && paramsTimeslot !== newTimeslotHHmm && { timeslot: newTimeslotHHmm }),
      ...(meal && paramsMeal !== meal && { meal }),
      ...(date && paramsDate !== date && { date }),
      ...(newAllergens !== paramsAllergens && { allergens: newAllergens }),
      ...(newDietaries !== paramsDietaries && { dietaries: newDietaries }),
      ...(newVendors !== paramsVendors && { vendors: newVendors }),
    }
    if (Object.keys(newParams).length === 0) return
    const queryString = createQueryString(newParams)
    router.push(`${pathname}?${queryString}`)
  }, [
    date,
    meal,
    timeslot,
    timezone,
    paramsTimeslot,
    paramsDate,
    paramsMeal,
    paramsAllergens,
    paramsDietaries,
    paramsVendors,
    router,
    pathname,
    createQueryString,
    filters.allergens,
    filters.vendors,
    filters.dietaries,
    userDataError,
  ])

  if (!locale) throw new Error('LOCALE_REQUIRED')

  const currencyForLocaleCountry = useDefaultCurrency(userData)

  const isInitialLoad =
    !userDataCalled || (loadingUserData && !userData) || (loadingMenuData && (!meal || !date))

  const hasErrors = Boolean(userDataError || menuDataError)

  const reload = useCallback(async () => {
    await refetchUserData()
    await refetchMenuData()
  }, [refetchMenuData, refetchUserData])

  const onOrderPlacement = useCallback(
    ({ success, forwardUrl }: { success: boolean; forwardUrl: string }) => {
      if (success && forwardUrl) {
        router.push(`${forwardUrl}`)
      } else {
        reload()
      }
    },
    [reload, router],
  )
  if (
    hasErrors &&
    !(userDataError as CCMenuUserStateCustomError)?.redirect &&
    (userDataError?.message || menuDataError?.message)
  )
    return (
      <CanteenError
        message={userDataError?.message ?? menuDataError?.message}
        code={userDataError?.name ?? menuDataError?.name}
      />
    )
  if (isInitialLoad) return <LoadingIndicator />
  return (
    <>
      {userData && !userDataError && !menuDataError && date && meal ? (
        <>
          {useNewCcUi ? (
            <MenuViewV2
              appPromo={appPromo}
              onClearFilters={clearFilters}
              menuFilters={filters}
              searchTerm={searchTerm}
              onToggleFilterValue={toggleFilterValue}
              budgetFilter={budgetFilter}
              onToggleBudgetFilter={toggleBudgetFilter}
              onSearchTermChange={onSearchTermChange}
              language={getLangFromLocale(locale) ?? Language.En}
              orderTimeRemaining={orderTimeRemaining}
              menuClosed={menuClosed}
              onChangeTimeslot={updateTimeslot}
              onChangeAccountLocation={(accountLocationId) => {
                updateAccountLocation(accountLocationId)
              }}
              loadingSelectedServiceData={loadingMenuData || updatingLocation}
              onChangeSelectedService={updateSelectedService}
              currency={currencyForLocaleCountry}
              meal={meal}
              date={date}
              timeslot={timeslot}
              menuData={menuData}
              userData={userData}
              userId={userDataProp?.id ?? ''}
              onOrderPlacement={onOrderPlacement}
              reloadFromApi={reload}
            />
          ) : (
            <MenuView
              onClearFilters={clearFilters}
              menuFilters={filters}
              onToggleFilterValue={toggleFilterValue}
              language={getLangFromLocale(locale) ?? Language.En}
              orderTimeRemaining={orderTimeRemaining}
              menuClosed={menuClosed}
              onChangeTimeslot={updateTimeslot}
              onChangeAccountLocation={(accountLocationId) => {
                updateAccountLocation(accountLocationId)
              }}
              loadingSelectedServiceData={loadingMenuData || updatingLocation}
              onChangeSelectedService={updateSelectedService}
              currency={currencyForLocaleCountry}
              meal={meal}
              date={date}
              timeslot={timeslot}
              menuData={menuData}
              userData={userData}
              onOrderPlacement={onOrderPlacement}
              reloadFromApi={reload}
            />
          )}
        </>
      ) : null}
    </>
  )
}
